import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Diamond, 
  CheckCircle2, 
  Package,
  Truck,
  MapPin,
  CreditCard,
  Download,
  ArrowRight
} from "lucide-react";
import { resolveImageUrl } from "../lib/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
    } catch (err) {
      console.error("Error fetching order:", err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!order && orderId) {
      fetchOrder();
    }
  }, [orderId, order, fetchOrder]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const getDeliveryDate = () => {
    const today = new Date();
    let days = 7;
    if (order?.delivery_method === "express") days = 3;
    if (order?.delivery_method === "overnight") days = 1;
    today.setDate(today.getDate() + days);
    return today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080809] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#080809] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#71717A] mb-4">Order not found</p>
          <Link to="/shop" className="btn-primary">Return to Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080809]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="confirmation-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <Diamond className="w-8 h-8 text-primary" />
              <span className="text-xl font-light tracking-tight text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                VK Gems & Jewels
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Success Header */}
          <div className="text-center mb-12" data-testid="confirmation-header">
            <div className="w-20 h-20 bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-[#F5F5F0] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Thank You for Your Purchase!
            </h1>
            <p className="text-lg text-[#A1A1AA]">
              Your order has been confirmed and will be shipped soon.
            </p>
          </div>

          {/* Order Number Banner */}
          <div className="glass-card p-6 mb-8 text-center" data-testid="order-number-banner">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Order Confirmation Number</p>
            <p className="text-2xl text-primary font-mono" data-testid="order-number">{order.order_number}</p>
            <p className="text-sm text-[#71717A] mt-2">
              Placed on {formatDate(order.created_at)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Shipping Info */}
            <div className="glass-card p-6" data-testid="shipping-info">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-primary" />
                <h3 className="text-lg text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Shipping Address</h3>
              </div>
              <div className="text-[#A1A1AA] space-y-1">
                <p className="text-[#F5F5F0]">{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                <p>{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</p>
                <p>{order.shipping_address.country}</p>
                <p className="pt-2">{order.shipping_address.email}</p>
                <p>{order.shipping_address.phone}</p>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="glass-card p-6" data-testid="delivery-info">
              <div className="flex items-center gap-3 mb-4">
                <Truck className="w-5 h-5 text-primary" />
                <h3 className="text-lg text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Delivery Method</h3>
              </div>
              <div className="text-[#A1A1AA]">
                <p className="text-[#F5F5F0] capitalize mb-1">{order.delivery_method} Shipping</p>
                <p className="text-sm">Estimated delivery: {getDeliveryDate()}</p>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <h3 className="text-lg text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Payment</h3>
                </div>
                <p className="text-[#A1A1AA] capitalize">
                  {order.payment_method === "card" ? `Credit Card ending in ${order.card_last_four}` : "PayPal"}
                </p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="glass-card p-6 mb-8" data-testid="order-items">
            <div className="flex items-center gap-3 mb-6">
              <Package className="w-5 h-5 text-primary" />
              <h3 className="text-lg text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Order Items</h3>
            </div>
            
            <div className="space-y-4">
              {order.items.map((item, idx) => (
                <div key={`${item.product_id}-${item.selected_gem_variant || ""}-${idx}`} className="flex gap-4 p-4 bg-[#0a0a0b] border border-white/5">
                  <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                    <img src={resolveImageUrl(item.product_image_url)} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[#F5F5F0]">{item.title}</h4>
                    {item.selected_gem_variant && (
                      <p className="text-xs text-primary">{item.selected_gem_variant}</p>
                    )}
                    <p className="text-sm text-[#71717A]">Quantity: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#F5F5F0]">{formatPrice(item.subtotal)}</p>
                    <p className="text-xs text-[#71717A]">{formatPrice(item.price)} each</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Totals */}
            <div className="border-t border-white/10 mt-6 pt-6 space-y-2">
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Subtotal</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Tax</span>
                <span>{formatPrice(order.tax)}</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Shipping</span>
                <span>{formatPrice(order.shipping_cost)}</span>
              </div>
              <div className="flex justify-between text-xl text-[#F5F5F0] pt-4 border-t border-white/10">
                <span>Total</span>
                <span className="text-primary" data-testid="order-total">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/shop" 
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button 
              onClick={() => window.print()}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="print-receipt-btn"
            >
              <Download className="w-4 h-4" />
              Print Receipt
            </button>
          </div>

          {/* Support Info */}
          <div className="text-center mt-12 pt-8 border-t border-white/5">
            <p className="text-[#71717A] text-sm">
              Questions about your order? Contact us at{" "}
              <a href="mailto:info@vkgemsandjewels.com" className="text-primary hover:underline">
                info@vkgemsandjewels.com
              </a>{" "}
              or call{" "}
              <a href="tel:+919555337978" className="text-primary hover:underline">
                +91 9555337978
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
